import React, { useEffect, useState } from "react";
import Instance from "../Services/Instance";
import config from "../Services/config.js";
import { Eye, CheckCircle, XCircle, Clock, AlertCircle, Users, Filter } from "lucide-react";
import SideBar from "../Layouts/SideBar";
import "../Assets/Styles/WithdrawalRequestsManager.css";

function WithdrawalRequestsManager() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState('admin');
  const [statusFilter, setStatusFilter] = useState('all');

  const getUserFullName = (userId) => {
    if (!userId) return "N/A";
    if (typeof userId === 'object' && userId.firstName && userId.lastName) {
      return `${userId.firstName} ${userId.lastName}`;
    }
    return userId.toString();
  };

  const getUserBalance = (userId) => {
    if (!userId || typeof userId !== 'object' || !userId.balance) return 0;
    return userId.balance;
  };

  const formatCreatedDate = (createdAt) => {
    if (!createdAt) return "-";
    const dateObject = new Date(createdAt);
    return dateObject.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMethodeLabel = (methode) => {
    switch (methode) {
      case "input": return "Dépôt";
      case "output": return "Retrait";
      default: return methode;
    }
  };

  const getMethodeClass = (methode) => {
    switch (methode) {
      case "input": return "method-input";
      case "output": return "method-output";
      default: return "method-default";
    }
  };

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        let result;
        
        if (viewMode === 'admin') {
          result = await Instance.get(`${config.api_url}/getAllRequest`);
          const allRequests = result.data.getrequestForWithdraw || [];
          
          // Filtrer pour exclure les dépôts non validés
          const filteredRequests = allRequests.filter(request => {
            // Si c'est un dépôt (input), ne montrer que s'il est validé ou approuvé
            if (request.methode === 'input') {
              return request.topUpStatus === 'validate' || request.topUpStatus === 'approved';
            }
            // Si c'est un retrait (output), montrer tous les statuts
            return true;
          });
          
          setRequests(filteredRequests);
        } else {
          result = await Instance.get(`${config.api_url}/withdrawsByUser`);
          const userRequests = result.data.withdrawals || result.data || [];
          
          // Filtrer pour exclure les dépôts non validés
          const filteredRequests = userRequests.filter(request => {
            if (request.methode === 'input') {
              return request.topUpStatus === 'validate' || request.topUpStatus === 'approved';
            }
            return true;
          });
          
          setRequests(filteredRequests);
        }
      } catch (error) {
        setError(error.response?.data?.message || error.message || "Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [viewMode]);

  const approveRequest = async (requestId) => {
    try {
      await Instance.put(`${config.api_url}/withdraw/approve/${requestId}`);
      setRequests(prev => prev.map(req => 
        req._id === requestId 
          ? { ...req, topUpStatus: "validate" }
          : req
      ));
      setShowModal(false);
      alert("Demande approuvée avec succès");
    } catch (error) {
      setError(error.response?.data?.message || error.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "status-pending";
      case "Not Validate": return "status-pending";
      case "En attente": return "status-pending";
      case "Terminé": return "status-approved";
      case "approved": return "status-approved";
      case "Rejeté": return "status-rejected";
      case "rejected": return "status-rejected";
      case "validate": return "status-validate";
      default: return "status-default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
      case "Not Validate":
      case "En attente":
        return <Clock className="status-icon" />;
      case "Terminé":
      case "approved":
      case "validate":
        return <CheckCircle className="status-icon" />;
      default:
        return <AlertCircle className="status-icon" />;
    }
  };

  const showRequestDetails = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  const requestsArray = Array.isArray(requests) ? requests : [];
  
  const filteredRequests = requestsArray.filter(request => {
    if (statusFilter !== 'all' && statusFilter !== 'input' && statusFilter !== 'output') {
      if (request.topUpStatus !== statusFilter) return false;
    }
    
    if (statusFilter === 'input') {
      return request.methode === 'input';
    }
    if (statusFilter === 'output') {
      return request.methode === 'output';
    }
    
    return true;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="wrapper">
        <SideBar />
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="withdrawal-manager">
      <SideBar />
      
      <div className="main-content">
        <div className="content-wrapper">
          <div className="card">
            <div className="header">
              <div className="header-content">
                <div className="header-text">
                  <h1 className="header-title">Portefeuille utilisateurs</h1>
                  <p className="header-subtitle">Historique des opérations et requêtes</p>
                </div>
                
                <div className="header-controls">
                  <div className="view-toggle">
                    <button
                      onClick={() => setViewMode('admin')}
                      className={`toggle-btn ${viewMode === 'admin' ? 'active' : ''}`}
                    >
                      <Users className="btn-icon" />
                      Admin (Toutes)
                    </button>
                  </div>
                  
                  <div className="filter-container">
                    <Filter className="filter-icon" />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">Tous les statuts</option>
                      <option value="pending">En attente (pending)</option>
                      <option value="Not Validate">Non validé</option>
                      <option value="validate">Approuvé</option>
                      <option value="input">Dépôts uniquement</option>
                      <option value="output">Retraits uniquement</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="stats-grid">
                <div className="stat-card stat-total">
                  <div className="stat-label">Total</div>
                  <div className="stat-value">{requestsArray.length}</div>
                </div>
                <div className="stat-card stat-input">
                  <div className="stat-label">Dépôts</div>
                  <div className="stat-value">
                    {requestsArray.filter(r => r.methode === 'input').length}
                  </div>
                </div>
                <div className="stat-card stat-output">
                  <div className="stat-label">Retraits</div>
                  <div className="stat-value">
                    {requestsArray.filter(r => r.methode === 'output').length}
                  </div>
                </div>
                <div className="stat-card stat-pending">
                  <div className="stat-label">En attente</div>
                  <div className="stat-value">
                    {requestsArray.filter(r => r.topUpStatus === 'pending' || r.topUpStatus === 'Not Validate').length}
                  </div>
                </div>
                <div className="stat-card stat-approved">
                  <div className="stat-label">Approuvé</div>
                  <div className="stat-value">
                    {requestsArray.filter(r => r.topUpStatus === 'validate').length}
                  </div>
                </div>
              </div>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Utilisateur</th>
                    <th>Solde</th>
                    <th>Type</th>
                    <th>Montant</th>
                    <th>Date émission</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((request) => (
                    <tr key={request._id}>
                      <td>
                        <div className="cell-truncate" title={request._id}>
                          {request._id}
                        </div>
                      </td>
                      <td>
                        <div className="cell-truncate" title={getUserFullName(request.userId)}>
                          {getUserFullName(request.userId)}
                        </div>
                      </td>
                      <td className="balance-cell">
                        {getUserBalance(request.userId).toLocaleString('fr-FR')} FCFA
                      </td>
                      <td className={`method-cell ${getMethodeClass(request.methode)}`}>
                        <div className="type-with-method">
                          <span className="type-label">{getMethodeLabel(request.methode)}</span>
                          {request.methode === 'input' && request.topUpPaymentMethod && (
                            <span className="payment-method-badge">{request.topUpPaymentMethod}</span>
                          )}
                        </div>
                      </td>
                      <td className="amount-cell">
                        {request.montant} FCFA
                      </td>
                      <td className="date-cell">
                        {formatCreatedDate(request.createdAt)}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusColor(request.topUpStatus)}`}>
                          {getStatusIcon(request.topUpStatus)}
                          {request.topUpStatus}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => showRequestDetails(request)}
                            className="action-btn btn-view"
                            title="Voir les détails"
                          >
                            <Eye className="btn-icon" />
                          </button>
                          {viewMode === 'admin' && 
                           request.methode === 'output' && 
                           (request.topUpStatus === "pending" || request.topUpStatus === "Not Validate") && (
                            <button
                              onClick={() => approveRequest(request._id)}
                              className="action-btn btn-approve"
                              title="Approuver"
                            >
                              <CheckCircle className="btn-icon" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <div className="pagination-info">
                <p>
                  Affichage de <span className="font-medium">{indexOfFirstItem + 1}</span> à{' '}
                  <span className="font-medium">{Math.min(indexOfLastItem, filteredRequests.length)}</span> sur{' '}
                  <span className="font-medium">{filteredRequests.length}</span> opérations
                  {statusFilter !== 'all' && (
                    <span className="filter-info"> (filtrées par: {statusFilter})</span>
                  )}
                </p>
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn pagination-prev"
                >
                  ‹
                </button>
                
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const pageNumber = i + 1;
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => paginate(pageNumber)}
                      className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn pagination-next"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>

        {showModal && selectedRequest && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>Détails de l'opération</h3>
                <button onClick={() => setShowModal(false)} className="modal-close">
                  <XCircle className="close-icon" />
                </button>
              </div>
              
              <div className="modal-body">
                <div className="modal-item">
                  <span className="modal-label">Utilisateur: </span>
                  <span className="modal-value">{getUserFullName(selectedRequest.userId)}</span>
                </div>

                <div className="modal-item">
                  <span className="modal-label">Solde: </span>
                  <span className="modal-value balance-value">{getUserBalance(selectedRequest.userId).toLocaleString('fr-FR')} FCFA</span>
                </div>

                <div className="modal-item">
                  <span className="modal-label">Type d'opération: </span>
                  <span className={`modal-value ${getMethodeClass(selectedRequest.methode)}`}>
                    {getMethodeLabel(selectedRequest.methode)}
                  </span>
                </div>

                {selectedRequest.methode === 'input' && selectedRequest.topUpPaymentMethod && (
                  <div className="modal-item">
                    <span className="modal-label">Méthode de paiement: </span>
                    <span className="modal-value payment-method-highlight">{selectedRequest.topUpPaymentMethod}</span>
                  </div>
                )}
                
                <div className="modal-item">
                  <span className="modal-label">Statut: </span>
                  <span className={`status-badge ${getStatusColor(selectedRequest.topUpStatus)}`}>
                    {selectedRequest.topUpStatus}
                  </span>
                </div>
                
                <div className="modal-item">
                  <span className="modal-label">Montant: </span>
                  <span className="modal-value">{selectedRequest.montant} FCFA</span>
                </div>

                <div className="modal-item">
                  <span className="modal-label">Référence: </span>
                  <span className="modal-value">{selectedRequest.topUpRef || "-"}</span>
                </div>

                <div className="modal-item">
                  <span className="modal-label">Date de création: </span>
                  <span className="modal-value">{formatCreatedDate(selectedRequest.createdAt)}</span>
                </div>
                
                {viewMode === 'admin' && 
                 selectedRequest.methode === 'output' && 
                 (selectedRequest.topUpStatus === "pending" || selectedRequest.topUpStatus === "Not Validate") && (
                  <div className="modal-actions">
                    <button
                      onClick={() => approveRequest(selectedRequest._id)}
                      className="modal-btn btn-approve-modal"
                    >
                      Approuver le retrait
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="error-notification">
            <p className="error-title">Erreur</p>
            <p className="error-message">{typeof error === 'string' ? error : error.response?.data?.message || error.message}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default WithdrawalRequestsManager;